import { useState, useCallback, useRef } from "react";

// Promise-based confirmation hook. Pair with <ConfirmModal {...confirmProps} />.
//
// Usage:
//   const { confirm, confirmProps } = useConfirm();
//   const ok = await confirm({ title, message, confirmLabel, confirmVariant });
//   if (!ok) return;
//   // ...action...
//
// confirmProps already includes show / onConfirm / onCancel — spread it onto
// <ConfirmModal>. The promise resolves true on confirm, false on cancel or
// modal close.
export function useConfirm() {
  const [state, setState] = useState({
    show: false,
    title: "Confirm",
    message: "",
    confirmLabel: "Continue",
    cancelLabel: "Cancel",
    confirmVariant: "primary",
  });
  const resolverRef = useRef(null);

  const resolve = useCallback((value) => {
    if (resolverRef.current) {
      resolverRef.current(value);
      resolverRef.current = null;
    }
    setState((s) => ({ ...s, show: false }));
  }, []);

  const confirm = useCallback((opts = {}) => {
    return new Promise((res) => {
      resolverRef.current = res;
      setState({
        show: true,
        title: opts.title || "Confirm",
        message: opts.message || "",
        confirmLabel: opts.confirmLabel || "Continue",
        cancelLabel: opts.cancelLabel || "Cancel",
        confirmVariant: opts.confirmVariant || "primary",
      });
    });
  }, []);

  const onConfirm = useCallback(() => resolve(true), [resolve]);
  const onCancel = useCallback(() => resolve(false), [resolve]);

  return {
    confirm,
    confirmProps: { ...state, onConfirm, onCancel },
  };
}
